import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Button,
  Table,
  Form,
  Spinner,
  Alert,
  Badge,
  InputGroup,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import {
  FaPlus,
  FaTimes,
  FaBuilding,
  FaSearch,
  FaLink,
  FaUnlink,
  FaCheck,
  FaUsers,
} from "react-icons/fa";
import { AppDispatch, RootState } from "../../../store";
import { Company } from "../../../features/companies/companiesService";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import {
  addAgencyClientRelation,
  getCompanies,
  getCompany,
  removeAgencyClientRelation,
  clearError,
} from "../../../features/companies/companiesSlice";

interface ClientCompaniesModalProps {
  show: boolean;
  onHide: () => void;
  agencyCompany: Company;
}

const ClientCompaniesModal: React.FC<ClientCompaniesModalProps> = ({
  show,
  onHide,
  agencyCompany,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Modal'ın kendi state'inde agency company bilgisini tutalım
  const [localAgencyCompany, setLocalAgencyCompany] = useState(agencyCompany);
  const [isModalInitialized, setIsModalInitialized] = useState(false);

  // Tüm şirketleri çekiyoruz
  const { companies, isLoading, error } = useSelector(
    (state: RootState) => state.companies
  );

  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState<number | null>(null);


  // Modal açıldığında sadece bir kez initialize et
  useEffect(() => {
    if (show && agencyCompany && !isModalInitialized) {
      setLocalAgencyCompany(agencyCompany);
      setIsModalInitialized(true);
    }
  }, [show, agencyCompany, isModalInitialized]);

  // Modal kapandığında state'i reset et
  useEffect(() => {
    if (!show) {
      setIsModalInitialized(false);
      setSearch("");
      setSelectedClientIds([]);
      setShowAddModal(false);
      setIsLinking(false);
      setIsUnlinking(null);
    }
  }, [show]);

  // Modal açıldığında error'u temizle
  useEffect(() => {
    if (show) {
      dispatch(clearError());
    }
  }, [show, dispatch]);

  // Tüm şirketleri yükle
  useEffect(() => {
    if (show && (!companies || companies.length === 0)) {
      dispatch(getCompanies({ company_type: "client", is_active: true }));
    }
  }, [show, companies, dispatch]);

  // Client şirketleri filtrele
  const clientCompanies = companies.filter(
    (company) => company.company_type.toLowerCase() === "client"
  );

  // Bağlı client şirketleri bul
  const linkedClients = clientCompanies.filter((company) =>
    localAgencyCompany.managed_companies?.includes(company.id)
  );

  // Bağlı olmayan client şirketleri
  const unlinkedClients = clientCompanies.filter(
    (company) => !localAgencyCompany.managed_companies?.includes(company.id)
  );

  // Arama filtresine göre bağlı olmayan client şirketleri
  const unlinkedFilteredClients = unlinkedClients.filter((company) =>
    company.company_name.toLowerCase().includes(search.toLowerCase())
  );

  // Client şirketi bağlama işlevi (multiple)
  const handleLinkClients = async () => {
    if (selectedClientIds.length === 0) return;

    setIsLinking(true);
    try {
      // Her seçili client için ayrı ayrı link işlemi yap
      for (const clientId of selectedClientIds) {
        await dispatch(
          addAgencyClientRelation({
            agency_id: localAgencyCompany.id,
            managed_company_id: clientId,
          })
        ).unwrap();
      }
      
      // Local agency company'yi güncelle - yeni client'ları ekle
      setLocalAgencyCompany(prev => {
        const updatedManagedCompanies = [
          ...(prev.managed_companies || []),
          ...selectedClientIds
        ];
        return {
          ...prev,
          managed_companies: updatedManagedCompanies
        };
      });
      
      // Sadece Add modal'ı kapat ve state'i temizle
      setShowAddModal(false);
      setSelectedClientIds([]);
      setSearch("");
    } catch (error) {
      console.error("Error linking clients:", error);
    } finally {
      setIsLinking(false);
    }
  };

  // Client şirketi bağlantısını kaldırma işlevi
  const handleUnlinkClient = async (clientId: number) => {
    setIsUnlinking(clientId);
    try {
      await dispatch(
        removeAgencyClientRelation({
          agency_id: localAgencyCompany.id,
          managed_company_id: clientId,
        })
      ).unwrap();
      
      
      // Local agency company'yi güncelle - client'ı çıkar
      setLocalAgencyCompany(prev => {
        const updatedManagedCompanies = (prev.managed_companies || []).filter(
          id => id !== clientId
        );
        return {
          ...prev,
          managed_companies: updatedManagedCompanies
        };
      });
    } catch (error: any) {
      console.error("Error unlinking client:", error);
    } finally {
      setIsUnlinking(null);
    }
  };

  // Client seçme/seçimi kaldırma
  const handleClientSelection = (clientId: number) => {
    setSelectedClientIds(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  // Tümünü seç/seçimi kaldır
  const handleSelectAll = () => {
    if (selectedClientIds.length === unlinkedFilteredClients.length) {
      setSelectedClientIds([]);
    } else {
      setSelectedClientIds(unlinkedFilteredClients.map(client => client.id));
    }
  };

  // Modal'ı sadece kullanıcı manuel olarak kapattığında kapat
  const handleCloseModal = () => {
    onHide();
  };

  return (
    <>
      {/* Ana Modal - Bağlı Client Companies */}
      <Modal
        show={show}
        onHide={handleCloseModal}
        size="xl"
        backdrop={false}
        keyboard={false}
        className="client-companies-modal"
      >
        <Modal.Header className="border-0 pt-4 pb-0 px-4">
          <div className="d-flex align-items-center w-100">
            <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
              <Icon icon={FaBuilding} className="text-info" size="1.5rem" />
            </div>
            <div className="flex-grow-1">
              <Modal.Title className="mb-0 fw-bold h4">
                {t("client_companies")} - {localAgencyCompany.company_name}
              </Modal.Title>
              <p className="text-muted mb-0 mt-1">
                {t("manage_client_companies_for_agency")}
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={handleCloseModal}
              className="border-0 rounded-circle p-2 ms-3"
              style={{ width: "40px", height: "40px" }}
            >
              <Icon icon={FaTimes} />
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          {error && (
            <Alert variant="danger" className="mb-4">
              {typeof error === 'string' ? error : 'An error occurred while processing your request.'}
            </Alert>
          )}

          <Row className="mb-4">
            <Col md={6}>
              <Card className="stats-card">
                <Card.Body className="text-center">
                  <div className="stats-icon">
                    <Icon icon={FaUsers} className="text-primary" size="2rem" />
                  </div>
                  <h3 className="stats-number">{linkedClients.length}</h3>
                  <p className="stats-label">{t("linked_clients")}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="stats-card">
                <Card.Body className="text-center">
                  <div className="stats-icon">
                    <Icon icon={FaBuilding} className="text-success" size="2rem" />
                  </div>
                  <h3 className="stats-number">{unlinkedClients.length}</h3>
                  <p className="stats-label">{t("available_clients")}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 fw-semibold">
              {t("linked_client_companies")} ({linkedClients.length})
            </h5>
            <Button
              variant="primary"
              className="rounded-3 d-flex align-items-center"
              onClick={() => setShowAddModal(true)}
            >
              <Icon icon={FaPlus} className="me-2" />
              {t("add_client_company")}
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">{t("loading_companies")}</p>
            </div>
          ) : linkedClients.length === 0 ? (
            <Alert variant="info" className="text-center py-4">
              <Icon icon={FaBuilding} className="mb-3" size="3rem" />
              <h5>{t("no_linked_clients")}</h5>
              <p className="mb-3">
                {t("no_linked_clients_message", {
                  agencyName: localAgencyCompany.company_name,
                })}
              </p>
              <Button
                variant="primary"
                onClick={() => setShowAddModal(true)}
                className="rounded-3"
              >
                <Icon icon={FaPlus} className="me-2" />
                {t("add_first_client")}
              </Button>
            </Alert>
          ) : (
            <Table className="linked-clients-table" hover responsive>
              <thead>
                <tr>
                  <th className="linked-table-header">{t("client_company")}</th>
                  <th className="linked-table-header">{t("industry")}</th>
                  <th className="linked-table-header">{t("location")}</th>
                  <th className="linked-table-header">{t("status")}</th>
                  <th className="linked-table-header text-end">
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {linkedClients.map((company) => (
                  <tr key={company.id} className="linked-table-row">
                    <td className="linked-table-cell">
                      <div className="company-info">
                        <div className="company-name">
                          {company.company_name}
                        </div>
                        <div className="company-meta">
                          <span className="company-id">
                            #{company.id.toString().padStart(4, "0")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="linked-table-cell">
                      {company.industry || "-"}
                    </td>
                    <td className="linked-table-cell">
                      {company.city && company.country
                        ? `${company.city}, ${company.country}`
                        : company.city || company.country || "-"}
                    </td>
                    <td className="linked-table-cell">
                      <Badge 
                        bg={company.is_active ? "success" : "secondary"}
                        className="status-badge"
                      >
                        {company.is_active ? t("active") : t("inactive")}
                      </Badge>
                    </td>
                    <td className="linked-table-cell text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="unlink-btn"
                        onClick={() => handleUnlinkClient(company.id)}
                        disabled={isUnlinking === company.id}
                      >
                        {isUnlinking === company.id ? (
                          <Spinner animation="border" size="sm" className="me-2" />
                        ) : (
                          <Icon icon={FaUnlink} className="me-2" size={12} />
                        )}
                        {t("unlink")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      {/* Add Client Companies Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setSelectedClientIds([]);
          setSearch("");
        }}
        size="xl"
        backdrop="static"
        keyboard={false}
        className="add-clients-modal"
      >
        <Modal.Header className="border-0 pb-0 px-4 pt-4">
          <div className="d-flex align-items-center w-100">
            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
              <Icon icon={FaLink} className="text-primary" size="1.5rem" />
            </div>
            <div className="flex-grow-1">
              <Modal.Title className="mb-0 fw-bold h4">
                {t("add_client_company")}
              </Modal.Title>
              <p className="text-muted mb-0 mt-1">
                {t("select_clients_to_link")} ({selectedClientIds.length} {t("selected")})
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowAddModal(false);
                setSelectedClientIds([]);
                setSearch("");
              }}
              className="border-0 rounded-circle p-2 ms-3"
              style={{ width: "40px", height: "40px" }}
            >
              <Icon icon={FaTimes} />
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <InputGroup className="mb-4 search-input">
            <InputGroup.Text className="bg-light border-0">
              <Icon icon={FaSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t("search_clients")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 bg-light"
            />
          </InputGroup>

          {unlinkedFilteredClients.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Check
                type="checkbox"
                label={t("select_all")}
                checked={selectedClientIds.length === unlinkedFilteredClients.length}
                onChange={handleSelectAll}
                className="select-all-checkbox"
              />
              <Badge bg="primary" className="selected-count">
                {selectedClientIds.length} {t("selected")}
              </Badge>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">{t("loading_companies")}</p>
            </div>
          ) : unlinkedFilteredClients.length === 0 ? (
            <Alert variant="info" className="text-center py-4">
              <Icon icon={FaBuilding} className="mb-3" size="3rem" />
              <h5>{t("no_available_clients")}</h5>
              <p>
                {search
                  ? t("no_clients_matching_search")
                  : t("no_unlinked_clients_available")}
              </p>
            </Alert>
          ) : (
            <Table className="unlinked-clients-table" hover responsive>
              <thead>
                <tr>
                  <th className="unlinked-table-header" style={{ width: "50px" }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedClientIds.length === unlinkedFilteredClients.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="unlinked-table-header">
                    {t("client_company")}
                  </th>
                  <th className="unlinked-table-header">{t("industry")}</th>
                  <th className="unlinked-table-header">{t("location")}</th>
                  <th className="unlinked-table-header">{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {unlinkedFilteredClients.map((company) => (
                  <tr 
                    key={company.id} 
                    className={`unlinked-table-row ${selectedClientIds.includes(company.id) ? 'selected-row' : ''}`}
                    onClick={() => handleClientSelection(company.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="unlinked-table-cell">
                      <Form.Check
                        type="checkbox"
                        checked={selectedClientIds.includes(company.id)}
                        onChange={() => handleClientSelection(company.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="unlinked-table-cell">
                      <div className="company-info">
                        <div className="company-name">
                          {company.company_name}
                        </div>
                        <div className="company-meta">
                          <span className="company-id">
                            #{company.id.toString().padStart(4, "0")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="unlinked-table-cell">
                      {company.industry || "-"}
                    </td>
                    <td className="unlinked-table-cell">
                      {company.city && company.country
                        ? `${company.city}, ${company.country}`
                        : company.city || company.country || "-"}
                    </td>
                    <td className="unlinked-table-cell">
                      <Badge 
                        bg={company.is_active ? "success" : "secondary"}
                        className="status-badge"
                      >
                        {company.is_active ? t("active") : t("inactive")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 pb-4">
          <Button
            variant="secondary"
            onClick={() => {
              setShowAddModal(false);
              setSelectedClientIds([]);
              setSearch("");
            }}
            className="rounded-3"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleLinkClients}
            disabled={selectedClientIds.length === 0 || isLinking}
            className="rounded-3"
          >
            {isLinking ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t("linking")}...
              </>
            ) : (
              <>
                <Icon icon={FaLink} className="me-2" />
                {t("link_selected")} ({selectedClientIds.length})
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* Client Companies Modal Styles */
        .client-companies-modal .modal-content {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)"
          };
          border: none;
          border-radius: 20px;
          box-shadow: ${
            isDark
              ? "0 25px 50px rgba(0, 0, 0, 0.5)"
              : "0 25px 50px rgba(0, 0, 0, 0.15)"
          };
          backdrop-filter: blur(20px);
          z-index: 1050;
        }

        .add-clients-modal .modal-content {
          background: ${
            isDark ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.95)"
          };
          border: none;
          border-radius: 20px;
          box-shadow: ${
            isDark
              ? "0 25px 50px rgba(0, 0, 0, 0.5)"
              : "0 25px 50px rgba(0, 0, 0, 0.15)"
          };
          backdrop-filter: blur(20px);
          z-index: 1060;
        }

        .add-clients-modal .modal-backdrop {
          z-index: 1055;
        }

        /* Stats Cards */
        .stats-card {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.8)"
          };
          border: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
          };
          border-radius: 16px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .stats-card:hover {
          transform: translateY(-4px);
          box-shadow: ${
            isDark
              ? "0 8px 25px rgba(0, 0, 0, 0.3)"
              : "0 8px 25px rgba(0, 0, 0, 0.1)"
          };
        }

        .stats-icon {
          margin-bottom: 12px;
        }

        .stats-number {
          font-size: 2rem;
          font-weight: 700;
          color: ${isDark ? "#e2e8f0" : "#1f2937"};
          margin-bottom: 4px;
        }

        .stats-label {
          font-size: 0.875rem;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          margin: 0;
          font-weight: 500;
        }

        /* Table Styles */
        .linked-clients-table,
        .unlinked-clients-table {
          margin: 0;
          border-collapse: separate;
          border-spacing: 0;
          background: transparent;
          width: 100%;
        }

        .linked-table-header,
        .unlinked-table-header {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.5)" : "rgba(248, 250, 252, 0.8)"
          };
          padding: 16px 20px;
          font-weight: 600;
          color: ${isDark ? "#94a3b8" : "#6b7280"};
          font-size: 0.875rem;
          border-bottom: 2px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          };
          backdrop-filter: blur(10px);
        }

        .linked-table-row,
        .unlinked-table-row {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
        }

        .linked-table-row:hover,
        .unlinked-table-row:hover {
          background: ${
            isDark ? "rgba(51, 65, 85, 0.3)" : "rgba(248, 250, 252, 0.5)"
          };
          transform: translateY(-2px);
        }

        .selected-row {
          background: ${
            isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)"
          } !important;
          border-left: 4px solid #3b82f6;
        }

        .linked-table-cell,
        .unlinked-table-cell {
          padding: 16px 20px;
          border-bottom: 1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
          };
          font-size: 0.875rem;
          color: ${isDark ? "#e2e8f0" : "#374151"};
          vertical-align: middle;
        }

        /* Company Info */
        .company-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .company-name {
          font-weight: 600;
          color: ${isDark ? "#f1f5f9" : "#1f2937"};
        }

        .company-meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .company-id {
          font-size: 0.75rem;
          color: ${isDark ? "#64748b" : "#6b7280"};
          background: ${
            isDark ? "rgba(100, 116, 139, 0.2)" : "rgba(107, 114, 128, 0.1)"
          };
          padding: 2px 6px;
          border-radius: 4px;
        }

        /* Status Badge */
        .status-badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 6px;
        }

        /* Button styles */
        .link-btn, .unlink-btn {
          border-radius: 8px;
          padding: 6px 12px;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .link-btn:hover {
          background: ${isDark ? "#3b82f6" : "#3b82f6"};
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
        }

        .unlink-btn:hover {
          background: ${isDark ? "#ef4444" : "#ef4444"};
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.35);
        }

        /* Search input */
        .search-input {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: ${
            isDark
              ? "0 4px 12px rgba(0, 0, 0, 0.2)"
              : "0 4px 12px rgba(0, 0, 0, 0.05)"
          };
        }

        .search-input .form-control {
          height: 48px;
          background: ${isDark ? "rgba(51, 65, 85, 0.5)" : "#f8f9fa"};
          color: ${isDark ? "#e2e8f0" : "#374151"};
        }

        .search-input .input-group-text {
          background: ${isDark ? "rgba(51, 65, 85, 0.5)" : "#f8f9fa"};
          color: ${isDark ? "#94a3b8" : "#6b7280"};
        }

        /* Checkbox styles */
        .select-all-checkbox {
          margin: 0;
        }

        .select-all-checkbox .form-check-input {
          border-radius: 4px;
          border: 2px solid ${isDark ? "#64748b" : "#d1d5db"};
        }

        .select-all-checkbox .form-check-input:checked {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }

        .selected-count {
          font-size: 0.875rem;
          padding: 6px 12px;
          border-radius: 8px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .client-companies-modal .modal-dialog,
          .add-clients-modal .modal-dialog {
            margin: 10px;
            max-width: calc(100% - 20px);
          }
          
          .stats-card {
            margin-bottom: 16px;
          }
          
          .linked-table-cell,
          .unlinked-table-cell {
            padding: 12px 16px;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </>
  );
};

export default ClientCompaniesModal;
